import type {
  HeicConversionRequest,
  HeicConversionResponse,
} from "./HeicConverter.worker"

// Terminate the worker after this long without work, releasing the decoder's
// WebAssembly memory. Back-to-back conversions keep reusing the same worker.
const IDLE_TIMEOUT_IN_MS = 10_000

const HEIC_WORKER_ERROR = "HeicWorkerError"

interface PendingConversion {
  resolve: (blob: Blob) => void
  reject: (error: Error) => void
}

let worker: Worker | null = null
let idleTimeoutId: ReturnType<typeof setTimeout> | undefined
let nextRequestId = 0
const pendingConversions = new Map<number, PendingConversion>()

const createWorkerError = (message: string) =>
  Object.assign(new Error(message), { name: HEIC_WORKER_ERROR })

const terminateWorker = (reason: Error) => {
  clearTimeout(idleTimeoutId)
  worker?.terminate()
  worker = null

  pendingConversions.forEach(({ reject }) => reject(reason))
  pendingConversions.clear()
}

const scheduleIdleTermination = () => {
  clearTimeout(idleTimeoutId)
  if (pendingConversions.size === 0) {
    idleTimeoutId = setTimeout(
      () => terminateWorker(createWorkerError("HEIC worker terminated")),
      IDLE_TIMEOUT_IN_MS
    )
  }
}

const handleWorkerMessage = (event: MessageEvent<HeicConversionResponse>) => {
  const { id, blob, error } = event.data
  const pendingConversion = pendingConversions.get(id)
  pendingConversions.delete(id)

  if (pendingConversion) {
    if (blob) {
      pendingConversion.resolve(blob)
    } else {
      pendingConversion.reject(new Error(error ?? "unknown conversion error"))
    }
  }

  scheduleIdleTermination()
}

const getWorker = (): Worker => {
  clearTimeout(idleTimeoutId)

  if (!worker) {
    worker = new Worker(new URL("./HeicConverter.worker.ts", import.meta.url))
    worker.onmessage = handleWorkerMessage
    // the worker script failed to load or crashed: fail every pending
    // conversion so callers can fall back to converting in the page
    worker.onerror = event => {
      event.preventDefault()
      // a script that fails to load fires a plain event without a message
      const message = event.message || "the worker script failed to load"
      terminateWorker(createWorkerError(`HEIC worker error: ${message}`))
    }
  }

  return worker
}

export const HeicConverter = {
  isSupported(): boolean {
    return (
      typeof Worker !== "undefined" && typeof OffscreenCanvas !== "undefined"
    )
  },

  // true when the worker itself failed, as opposed to the image being
  // impossible to convert
  isWorkerError(error: unknown): boolean {
    return error instanceof Error && error.name === HEIC_WORKER_ERROR
  },

  // the buffer is transferred to the worker and unusable afterwards
  async convertToJpeg(buffer: ArrayBuffer, quality: number): Promise<Blob> {
    const id = nextRequestId++
    const request: HeicConversionRequest = { id, buffer, quality }

    // settled by handleWorkerMessage, or by terminateWorker on failure
    const conversion = new Promise<Blob>((resolve, reject) => {
      pendingConversions.set(id, { resolve, reject })
    })

    try {
      getWorker().postMessage(request, [buffer])
    } catch (error) {
      pendingConversions.delete(id)
      scheduleIdleTermination()
      const message = error instanceof Error ? error.message : String(error)
      throw createWorkerError(`unable to start HEIC worker: ${message}`)
    }

    return await conversion
  },
}
